import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, User, FileText, Download, Calendar, Briefcase,
  Fingerprint, AlertCircle, Loader2, Inbox, Megaphone, Pin,
  ShieldQuestion, FileDown, Trash2, Clock, CheckCircle2, XCircle, KeyRound,
} from 'lucide-react';
import api from '@utils/api';
import { useAuth } from '@contexts/AuthContext';
import { SecurityTab } from '@components/account/SecurityTab';

const TABS = [
  { id: 'profile',       label: 'Profile',       icon: User },
  { id: 'documents',     label: 'Documents',     icon: FileText },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
  { id: 'security',      label: 'Security',      icon: KeyRound },
  { id: 'privacy',       label: 'Privacy',       icon: ShieldQuestion },
];

const ERASURE_STATUS_STYLE = {
  pending:    { color: '#E8A000', bg: 'rgba(232,160,0,0.1)',  border: 'rgba(232,160,0,0.25)',  icon: Clock,        label: 'Pending review' },
  in_review:  { color: '#1A5E9E', bg: 'rgba(26,94,158,0.1)',  border: 'rgba(26,94,158,0.25)',  icon: Clock,        label: 'In review' },
  completed:  { color: '#22C55E', bg: 'rgba(34,197,94,0.1)',   border: 'rgba(34,197,94,0.25)',   icon: CheckCircle2, label: 'Completed' },
  rejected:   { color: '#EF4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.25)',   icon: XCircle,      label: 'Rejected' },
};

const DOC_TYPE_LABEL = {
  payslip:     'Payslip',
  contract:    'Contract',
  certificate: 'Certificate',
  circular:    'Circular',
};

function Card({ children, className = '' }) {
  return (
    <div
      className={`rounded-2xl p-6 ${className}`}
      style={{
        background:     'rgba(10,16,35,0.9)',
        border:         '1px solid rgba(255,255,255,0.07)',
        backdropFilter: 'blur(24px)',
        boxShadow:      '0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(45,125,70,0.06)',
      }}
    >
      {children}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: 'rgba(45,125,70,0.08)', color: '#2D7D46' }}
      >
        <Icon size={14} />
      </div>
      <div>
        <p className="text-2xs font-mono uppercase tracking-widest text-gl-muted">{label}</p>
        <p className="text-sm text-gl-ghost mt-0.5">{value ?? '—'}</p>
      </div>
    </div>
  );
}

function ProfileTab() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/staff/me')
      .then(({ data }) => setProfile(data.staff))
      .catch((err) => setError(err.response?.data?.message || 'Could not load your profile'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gl-muted">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
        <AlertCircle size={14} className="shrink-0" />
        {error}
      </div>
    );
  }

  return (
    <Card className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-display font-semibold text-gl-ghost">
            {profile.first_name} {profile.last_name}
          </h2>
          <p className="text-xs text-gl-muted font-mono mt-0.5">{profile.employee_id}</p>
        </div>
        <span
          className="text-2xs font-mono uppercase tracking-widest px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(34,197,94,0.1)', color: '#22C55E', border: '1px solid rgba(34,197,94,0.25)' }}
        >
          {profile.status}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field icon={Briefcase} label="Department"    value={profile.department} />
        <Field icon={Briefcase} label="Position"      value={profile.position} />
        <Field icon={Calendar}  label="Start Date"    value={profile.start_date?.slice(0, 10)} />
        <Field icon={Calendar}  label="Contract Type" value={profile.contract_type} />
        <Field icon={User}      label="Email"         value={profile.email} />
        <Field icon={User}      label="Phone"         value={profile.phone} />
      </div>

      <div className="pt-5 border-t border-white/5">
        <p className="text-2xs font-mono uppercase tracking-widest text-gl-muted mb-4">
          Sensitive data — visible only to you and HR
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field icon={Calendar}     label="Date of Birth"   value={profile.date_of_birth?.slice(0, 10)} />
          <Field icon={Fingerprint}  label="Passport Number" value={profile.passport_number} />
          <Field icon={Fingerprint}  label="ID Number"       value={profile.id_number} />
          <Field icon={User}         label="Nationality"     value={profile.nationality} />
        </div>
      </div>
    </Card>
  );
}

function DocumentsTab() {
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    api.get('/staff/me/documents')
      .then(({ data }) => setDocuments(data.documents))
      .catch((err) => setError(err.response?.data?.message || 'Could not load your documents'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = useCallback(async (id) => {
    setDownloadingId(id);
    try {
      const response = await api.get(`/staff/me/documents/${id}/download`, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'document';

      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Could not download this document');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gl-muted">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
        <AlertCircle size={14} className="shrink-0" />
        {error}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center py-16 text-center">
        <Inbox size={28} className="text-gl-muted mb-3" />
        <p className="text-sm text-gl-ghost">No documents yet</p>
        <p className="text-xs text-gl-muted mt-1">Payslips and contracts uploaded by HR will appear here.</p>
      </Card>
    );
  }

  return (
    <Card className="space-y-2">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="flex items-center justify-between p-3 rounded-xl transition-colors hover:bg-white/[0.03]"
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: 'rgba(45,125,70,0.08)', color: '#2D7D46' }}
            >
              <FileText size={14} />
            </div>
            <div>
              <p className="text-sm text-gl-ghost">{DOC_TYPE_LABEL[doc.type] || doc.type}</p>
              <p className="text-2xs text-gl-muted font-mono">
                {doc.period || doc.created_at?.slice(0, 10)}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleDownload(doc.id)}
            disabled={downloadingId === doc.id}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider transition-all"
            style={{ background: 'rgba(45,125,70,0.1)', border: '1px solid rgba(45,125,70,0.25)', color: '#2D7D46' }}
          >
            {downloadingId === doc.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            Download
          </button>
        </div>
      ))}
    </Card>
  );
}

function AnnouncementsTab() {
  const [announcements, setAnnouncements] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/comms/announcements')
      .then(({ data }) => setAnnouncements(data.announcements))
      .catch((err) => setError(err.response?.data?.message || 'Could not load announcements'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gl-muted">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 rounded-xl text-sm" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
        <AlertCircle size={14} className="shrink-0" />
        {error}
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <Card className="flex flex-col items-center justify-center py-16 text-center">
        <Megaphone size={28} className="text-gl-muted mb-3" />
        <p className="text-sm text-gl-ghost">No announcements</p>
        <p className="text-xs text-gl-muted mt-1">Company news and notices will appear here.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {announcements.map((a) => (
        <Card key={a.id} className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-sm font-display font-semibold text-gl-ghost">{a.title}</h3>
            {a.pinned && (
              <span
                className="flex items-center gap-1 text-2xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                style={{ background: 'rgba(232,160,0,0.1)', color: '#E8A000', border: '1px solid rgba(232,160,0,0.25)' }}
              >
                <Pin size={10} />
                Pinned
              </span>
            )}
          </div>
          <p className="text-sm text-gl-muted leading-relaxed whitespace-pre-wrap">{a.body}</p>
          <p className="text-2xs font-mono text-gl-muted pt-1">
            {a.author_name} · {a.published_at?.slice(0, 10)}
          </p>
        </Card>
      ))}
    </div>
  );
}

function PrivacyTab() {
  const [exporting, setExporting] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const loadRequests = useCallback(() => {
    setLoading(true);
    api.get('/staff/me/erasure-requests')
      .then(({ data }) => setRequests(data.erasureRequests))
      .catch((err) => setError(err.response?.data?.message || 'Could not load erasure requests'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const response = await api.get('/staff/me/export', { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'my-data-export.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Could not export your data');
    } finally {
      setExporting(false);
    }
  }, []);

  const handleSubmitErasure = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/staff/me/erasure-requests', { reason: reason || undefined });
      setReason('');
      setShowForm(false);
      loadRequests();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit erasure request');
    } finally {
      setSubmitting(false);
    }
  }, [reason, loadRequests]);

  const pendingExists = requests.some((r) => r.status === 'pending' || r.status === 'in_review');

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(45,125,70,0.08)', color: '#2D7D46' }}>
            <FileDown size={14} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-display font-semibold text-gl-ghost">Download my data</h3>
            <p className="text-xs text-gl-muted mt-1">
              Export everything held about you — profile, documents list, consents and erasure request history — as a JSON file.
            </p>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider transition-all"
          style={{ background: 'rgba(45,125,70,0.1)', border: '1px solid rgba(45,125,70,0.25)', color: '#2D7D46' }}
        >
          {exporting ? <Loader2 size={12} className="animate-spin" /> : <FileDown size={12} />}
          Export my data
        </button>
      </Card>

      <Card className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}>
            <Trash2 size={14} />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-display font-semibold text-gl-ghost">Request account erasure</h3>
            <p className="text-xs text-gl-muted mt-1">
              Submits a request for HR to review. Some records (e.g. payroll) may be subject to legal retention
              and can't be deleted immediately — HR will follow up on what's possible.
            </p>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg text-xs" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444' }}>
            <AlertCircle size={12} className="shrink-0" />
            {error}
          </div>
        )}

        {!showForm && !pendingExists && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider transition-all"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#EF4444' }}
          >
            <Trash2 size={12} />
            Request erasure
          </button>
        )}

        {pendingExists && !showForm && (
          <p className="text-xs text-gl-muted">You already have a request awaiting review — see status below.</p>
        )}

        {showForm && (
          <form onSubmit={handleSubmitErasure} className="space-y-3">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Optional — let HR know why (not required)"
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm text-gl-ghost placeholder-gl-muted bg-gl-surface border border-gl-border focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider transition-all"
                style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }}
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                Submit request
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-3 py-1.5 rounded-lg text-2xs font-mono uppercase tracking-wider text-gl-muted"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {!loading && requests.length > 0 && (
          <div className="pt-3 border-t border-white/5 space-y-2">
            <p className="text-2xs font-mono uppercase tracking-widest text-gl-muted">Request history</p>
            {requests.map((r) => {
              const style = ERASURE_STATUS_STYLE[r.status] || ERASURE_STATUS_STYLE.pending;
              const StatusIcon = style.icon;
              return (
                <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.02)' }}>
                  <div>
                    <p className="text-xs text-gl-ghost">{r.requested_at?.slice(0, 10)}</p>
                    {r.review_notes && <p className="text-2xs text-gl-muted mt-0.5">{r.review_notes}</p>}
                  </div>
                  <span
                    className="flex items-center gap-1 text-2xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                    style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
                  >
                    <StatusIcon size={10} />
                    {style.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

export function MyAccount() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  return (
    <div
      className="min-h-screen bg-gl-void px-4 py-8"
      style={{
        backgroundImage: `
          radial-gradient(ellipse at 30% 0%, rgba(45,125,70,0.05) 0%, transparent 50%),
          radial-gradient(ellipse at 70% 100%, rgba(26,94,158,0.04) 0%, transparent 50%)
        `,
      }}
    >
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-gl-muted hover:text-gl-ghost mb-6 transition-colors">
          <ArrowLeft size={12} />
          Back to hub
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-2xl font-display font-bold text-gl-ghost">My Account</h1>
          <p className="text-xs text-gl-muted mt-1">{user?.name}</p>

          <div className="flex items-center gap-1 mt-6 mb-6 p-1 rounded-xl w-fit" style={{ background: 'rgba(255,255,255,0.03)' }}>
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-display font-medium transition-all"
                style={
                  activeTab === id
                    ? { background: 'linear-gradient(135deg, #2D7D46, #1A5E9E)', color: '#0D1B2A' }
                    : { color: '#8199B4' }
                }
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === 'profile' && <ProfileTab />}
              {activeTab === 'documents' && <DocumentsTab />}
              {activeTab === 'announcements' && <AnnouncementsTab />}
              {activeTab === 'security' && <SecurityTab />}
              {activeTab === 'privacy' && <PrivacyTab />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
